import { AfterViewInit, Component, ElementRef, Inject, Input, OnInit, Renderer2, ViewEncapsulation, OnDestroy } from '@angular/core'
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser'
import { MsalService } from '@azure/msal-angular'
import { WE_DEFAULT_LOCALE, WE_LOCALE, WE_LOCALES } from './app.module'
import { TranslateService } from '@ngx-translate/core'
import { ApiService, UserInfo } from './services/api.service'
import { AccountType } from './interfaces/api/AccountType'
import { environment } from '../environments/environment'
import { SalesforceService } from './services/salesforce.service'
import { DOCUMENT } from '@angular/common'
import { jwtDecode } from 'jwt-decode'
import { firstValueFrom, Subscription } from 'rxjs'
import { Router, NavigationEnd, NavigationError } from '@angular/router'
import { MenuItemType } from './interfaces/api/MenuItemType'
import { UtmService } from './services/utm.service'
import { PlatformService, PlatformName } from './services/platform.service'

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    encapsulation: ViewEncapsulation.ShadowDom,
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() activeItemCode: string = 'home'
    @Input() customStyle: string = ''
    @Input() platform: PlatformName | undefined

    isWebComponent: boolean

    initialUrl: URL
    title = 'we-app'
    userInfo: UserInfo
    isTokenAuthComingFromUrl: boolean = false
    isInCanvas: boolean = false

    isMobileMenuOpen: boolean = false
    pageLoaded: boolean = false
    isLoginErrorRoute: boolean = false

    direction: string = 'ltr'

    // --- Fix: prevent double login-flow execution (esp. on Safari) ---
    private loginFlowRunning = false
    private loginFlowCompleted = false

    private routerSub?: Subscription
    private userInfoSub?: Subscription
    private msalCallbackId?: string | null
    private broadcastChannel?: BroadcastChannel
    iframeUrl: SafeResourceUrl | string = ''
    private currentLanguage: string = ''

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private renderer: Renderer2,
        private authService: MsalService,
        private translate: TranslateService,
        private api: ApiService,
        private elRef: ElementRef,
        private salesforce: SalesforceService,
        private router: Router,
        private sanitizer: DomSanitizer,
        private utmService: UtmService,
        private platformService: PlatformService,
    ) {
        this.isWebComponent = environment.webComponent
        this.initialUrl = new URL(window.location.href)

        translate.setDefaultLang(WE_DEFAULT_LOCALE.substring(0, 2))
        // Language initialization is handled in ngOnInit with proper priority:
        // 1. localStorage 'we-locale' (user explicit preference)
        // 2. userInfo.locale (Salesforce preference)
        // 3. URL param 'language' (WE_LOCALE)
        // 4. Default fallback
    }

    ngOnInit() {
        if (this.platform) {
            this.platformService.setPlatform(this.platform)
        }

        // Capture UTM parameters from URL and save to sessionStorage
        this.utmService.captureFromUrl(this.initialUrl)

        this.clearSalesforce()
        this.isInCanvas = this.salesforce.isCanvas()

        console.log('Updated version detected')

        // Track login-error route
        this.isLoginErrorRoute = this.router.url?.includes('login-error') || false

        this.routerSub = this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.isLoginErrorRoute = event.urlAfterRedirects?.includes('login-error') || false
            }

            if (event instanceof NavigationError) {
                try {
                    if (event.error && event.error.message) {
                        const errorMessage = event.error.message as string
                        console.error('navigation error caught error.message', errorMessage)

                        if (
                            errorMessage.includes('error loading dynamically imported module') ||
                            errorMessage.includes('Importing a module script failed') ||
                            errorMessage.includes('Failed to fetch dynamically imported module')
                        ) {
                            // Keep existing behavior (no forced reload), but log it
                            console.log('Old version detected')
                        }
                    } else {
                        console.error('navigation error', event)
                    }
                } catch (error) {
                    console.error('navigation error', event, error)
                }
            }
        })

        // Register MSAL callback early, but DO NOT re-run login flow twice.
        // We keep the event only for visibility and a safe "single-flight" re-run.
        this.msalCallbackId = this.authService.instance.addEventCallback((e) => {
            if (e.eventType === 'msal:handleRedirectEnd') {
                console.log('LOGIN FLOW REDIRECT END')
                if (this.isTokenAuthComingFromUrl) return
                // In case your ApiService does not internally handle redirect completion,
                // allow a re-entry but guarded to avoid duplicates.
                this.runLoginFlowOnce('redirectEnd')
            }
        })

        // Token auth from URL: do not trigger MSAL flow
        if (this.initialUrl.searchParams.has('token')) {
            const authToken = this.initialUrl.searchParams.get('token') || ''
            const codedToken = jwtDecode(authToken)

            const rawData = (codedToken as any).data
            const userData = JSON.parse(rawData) as UserInfo

            console.log('set user info from token on url')
            this.api.setUserInfo(authToken, userData)
            this.isTokenAuthComingFromUrl = true
            console.log(userData, 'userInfo')
            console.log(userData.impersonatedBy, 'userInfo')
        } else {
            // Normal flow (WebComponent vs regular)
            if (this.isWebComponent) {
                console.log('LOGIN FLOW START at webcomponent')
                // If webComponentLogin triggers MSAL internally, you might still want the guard there.
                // We leave as-is.
                this.api.webComponentLogin()
            } else {
                console.log('LOGIN FLOW START')
                this.activeItemCode = 'home'
                this.runLoginFlowOnce('start')
            }
        }

        // Language initialization with clear priority:
        // 1. localStorage 'we-locale' (user explicit preference)
        // 2. userInfo.locale (Salesforce preference) - handled in subscription
        // 3. URL param 'language' (WE_LOCALE)
        // 4. Default fallback
        const savedLangPreference = localStorage.getItem('we-locale')
        const urlLangParam = this.initialUrl.searchParams.get('language')

        // Subscribe to user info
        this.userInfoSub = this.api.userInfo.subscribe((userInfo) => {
            this.userInfo = userInfo
            this.updateIframeUrl()

            // Only checking contracts when opening in we
            if (!this.isWebComponent) {
                this.checkForCtopContract()
            }

            if (userInfo.accountType?.toString()) {
                document.body.classList.add(userInfo.accountType?.toString())
            }

            // Apply language with priority: localStorage > userInfo.locale > URL param > default
            this.initializeLanguage(savedLangPreference, userInfo.locale, urlLangParam)
        })

        // Canvas resize loop
        if (this.isInCanvas) {
            this.renderer.addClass(this.document.body, 'is-in-canvas')
            this.renderer.addClass(this.document.documentElement, 'is-in-canvas')

            setInterval(() => {
                this.salesforce.resize(this.elRef.nativeElement.parentElement.offsetHeight + 20)
            }, 100)
        }
    }

    ngAfterViewInit() {
        // ShadowDOM: ensure global stylesheet is applied inside shadow root
        const shadowRoot = this.elRef.nativeElement.shadowRoot
        const styleLink = document.createElement('link')
        styleLink.rel = 'stylesheet'
        styleLink.href = 'styles.css'
        shadowRoot.appendChild(styleLink)
    }

    ngOnDestroy() {
        this.routerSub?.unsubscribe()
        this.userInfoSub?.unsubscribe()

        // Clean up MSAL callback to avoid duplicates on hot reload / remount
        if (this.msalCallbackId) {
            this.authService.instance.removeEventCallback(this.msalCallbackId)
        }

        // Clean up BroadcastChannel
        if (this.broadcastChannel) {
            this.broadcastChannel.close()
        }
    }

    // ---- Fix: guard login flow to prevent duplicate openid-config calls ----
    private async runLoginFlowOnce(source: 'start' | 'redirectEnd') {
        // If token-auth via URL, never run MSAL flow
        if (this.isTokenAuthComingFromUrl) return

        // Prevent re-entrancy and repeated runs
        if (this.loginFlowRunning) {
            console.log(`LOGIN FLOW skipped (${source}) - already running`)
            return
        }

        this.loginFlowRunning = true
        try {
            // If handleLoginFlow returns void, this still works.
            // If it returns a Promise, we await it.
            await this.api.handleLoginFlow()
            this.loginFlowCompleted = true
            console.log(`LOGIN FLOW completed (${source})`)
        } catch (err: any) {
            // Allow retry if the flow failed (e.g., Safari glitch)
            this.loginFlowCompleted = false
            if(err && err.message && typeof err.message === 'string' && err.message.includes('interaction_in_progress')) {
                console.log('interaction_in_progress error. Allow retry', err)
            } else {
                console.error(`LOGIN FLOW failed (${source})`, err)
                this.router.navigate(['/login-error'])
            }
            // Keep existing behavior decisions in ApiService; we just guard duplicates.
        } finally {
            this.loginFlowRunning = false
        }
    }

    checkForCtopContract() {
        if (this.userInfo?.accountType && AccountType.isCtop(this.userInfo.accountType)) {
            this.api.getContractInfo('ctop').subscribe((contract) => {
                if (!contract.isSigned) {
                    const link = {
                        type: MenuItemType.ROUTE,
                        code: 'home',
                    }
                    this.router.navigate(['sign-contract', 'ctop'], { skipLocationChange: true, state: { link: link } })
                }
            })
        }
    }

    protected toggleMobileMenu() {
        this.isMobileMenuOpen = !this.isMobileMenuOpen
    }

    /**
     * Initialize language with clear priority order:
     * 1. localStorage 'we-locale' (user explicit preference - most recent choice)
     * 2. userInfo.locale (Salesforce user preference)
     * 3. URL param 'language'
     * 4. Default fallback (WE_DEFAULT_LOCALE)
     */
    private languageInitialized = false
    private async initializeLanguage(
        savedLangPreference: string | null,
        userLocale: string | null | undefined,
        urlLangParam: string | null
    ) {
        if(!savedLangPreference && !userLocale && !urlLangParam){
            console.log('No language available yet')
            return
        }

        // Only initialize once
        if (this.languageInitialized) {
            this.pageLoaded = true
            return
        }

        let languageToUse: string | null = null

        // Priority 1: localStorage (user's explicit choice)
        if (savedLangPreference) {
            languageToUse = savedLangPreference
            console.log('Language from localStorage:', savedLangPreference)
        }
        // Priority 2: userInfo.locale (Salesforce)
        else if (userLocale) {
            languageToUse = userLocale
            console.log('Language from userInfo.locale:', userLocale)
        }
        // Priority 3: URL param
        else if (urlLangParam) {
            languageToUse = urlLangParam.replace('_', '-').toLowerCase()
            console.log('Language from URL param:', languageToUse)
        }

        if (languageToUse) {
            await this.setLanguage(languageToUse)
        }

        this.languageInitialized = true
        this.pageLoaded = true
    }

    public async setLanguage(language?: string) {
        if (language) {
            const parsedLanguage = language.replace('_', '-').toLowerCase()
            if (WE_LOCALES.includes(parsedLanguage)) {
                await firstValueFrom(this.translate.use(parsedLanguage))
                console.log('Using language', parsedLanguage)
                this.setDir(parsedLanguage)
                this.syncLanguageToNexus(parsedLanguage)
                return
            } else {
                const languageCode = parsedLanguage.split('-')[0]
                if (WE_LOCALES.includes(languageCode)) {
                    await firstValueFrom(this.translate.use(languageCode))
                    console.log('Using language code', languageCode)
                    this.setDir(languageCode)
                    this.syncLanguageToNexus(languageCode)
                    return
                }
            }
        }
        await Promise.resolve()
    }

    setDir(language: string) {
        if (language && (language.toLowerCase().includes('iw') || language.toLowerCase().includes('he'))) {
            this.direction = 'rtl'
        } else {
            this.direction = 'ltr'
        }
    }

    /**
     * Synchronize language change to Nexus through multiple channels
     * This ensures that embedded applications like Nexus receive the language update
     * from We platform and display content in the selected language instead of
     * defaulting to Salesforce language preference.
     */
    private syncLanguageToNexus(language: string) {
        this.currentLanguage = language

        // 1. Write to localStorage['language'] - primary method
        localStorage.setItem('language', language)
        console.log('Language synced to localStorage:', language)

        // 2. Write to sessionStorage['cosentino-language'] - alternative method
        sessionStorage.setItem('cosentino-language', language)

        // 3. Emit custom event 'we-language-changed'
        const customEvent = new CustomEvent('we-language-changed', {
            detail: { language: language }
        })
        document.dispatchEvent(customEvent)
        console.log('Custom event "we-language-changed" dispatched:', language)

        // 4. Update web component property if available
        this.updateWebComponentLanguageProperty(language)

        // 5. Send postMessage to parent/other contexts
        try {
            window.parent.postMessage(
                { type: 'language-change', language: language },
                '*'
            )
            console.log('postMessage sent to parent:', language)
        } catch (error) {
            console.log('Could not send postMessage:', error)
        }

        // 6. Send via BroadcastChannel for cross-tab communication
        this.sendViaBroadcastChannel(language)
    }

    /**
     * Update the language property on the cos-we web component if available
     */
    private updateWebComponentLanguageProperty(language: string) {
        try {
            const weComponent = document.querySelector('cos-we') as any
            if (weComponent) {
                if (typeof weComponent.setLanguage === 'function') {
                    weComponent.setLanguage(language)
                    console.log('Web component setLanguage() called:', language)
                } else if (weComponent.hasOwnProperty('language')) {
                    weComponent.language = language
                    console.log('Web component language property set:', language)
                } else if (weComponent.hasOwnProperty('currentLanguage')) {
                    weComponent.currentLanguage = language
                    console.log('Web component currentLanguage property set:', language)
                }
            }
        } catch (error) {
            console.log('Could not update web component language property:', error)
        }
    }

    /**
     * Send language change via BroadcastChannel for cross-tab communication
     */
    private sendViaBroadcastChannel(language: string) {
        try {
            if (typeof BroadcastChannel !== 'undefined') {
                if (!this.broadcastChannel) {
                    this.broadcastChannel = new BroadcastChannel('cosentino-language')
                }
                this.broadcastChannel.postMessage({
                    type: 'language-change',
                    language: language
                })
                console.log('BroadcastChannel message sent:', language)
            }
        } catch (error) {
            console.log('Could not send BroadcastChannel message:', error)
        }
    }

    private loadCustomTranslations() {
        this.translate.get(['common.buttons.confirm', 'common.buttons.close', 'common.buttons.cancel'])
    }

    protected readonly AccountType = AccountType

    private clearSalesforce() {
        localStorage.removeItem('canvasInstanceId')
        localStorage.removeItem('canvasOauthToken')
        localStorage.removeItem('canvasTargetOrigin')
    }

    private updateIframeUrl() {
        const email = this.userInfo?.email ?? ''
        const language = this.userInfo?.language ?? ''

        if (!email || !language) {
            this.iframeUrl = ''
            return
        }

        const rawUrl =
            environment.cosentinoDotComUrl +
            '/wp-content/themes/b2c-child/js/getlocalstorage.php?param=' +
            encodeURIComponent(email) +
            '&language=' +
            encodeURIComponent(language)

        this.iframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(rawUrl)
        console.log('Updated iframe URL:', rawUrl)
    }
}
